import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { app as teamsApp } from '@microsoft/teams-js';
import mobileTelemetryService from './mobileTelemetry';

class ApplicationInsightsService {
  constructor() {
    this.reactPlugin = null;
    this.appInsights = null;
    this.isInitialized = false;
    this.platformContext = null;
  }

  /**
   * Early platform detection based on User Agent
   * Works without Teams SDK dependency
   */
  static detectPlatformFromUA(ua) {
    if (/TeamsMobile.*Android/i.test(ua)) return 'android';
    if (/TeamsMobile.*iOS|TeamsMobile.*iPhone/i.test(ua)) return 'ios';
    if (/Teams.*Electron/i.test(ua)) return 'desktop';
    if (/Teams/i.test(ua) && /Chrome|Edge/i.test(ua)) return 'web';
    if (/Teams/i.test(ua)) return 'teams-unknown';
    return 'browser';
  }

  /**
   * Initialize basic App Insights for early tracking
   * This runs before React and Teams SDK initialization
   */
  static initializeEarly() {
    // Only run in browser environment and if not already initialized
    if (typeof window === 'undefined' || window.appInsightsEarly) {
      return;
    }

    try {
      // Create a minimal App Insights instance for early tracking
      const earlyAI = new ApplicationInsights({
        config: {
          connectionString: 'InstrumentationKey=b1a59456-d7f5-4196-9fca-e4e9907585e3;IngestionEndpoint=https://switzerlandnorth-0.in.applicationinsights.azure.com/;LiveEndpoint=https://switzerlandnorth.livediagnostics.monitor.azure.com/;ApplicationId=b85ea7e7-ff33-481e-82b3-c5f2088aa23b',
          disableTelemetry: process.env.NODE_ENV === 'development',
          // Minimal config for early tracking
          disableExceptionTracking: false,
          enableAutoRouteTracking: false,
          disableAjaxTracking: true
        }
      });
      
      earlyAI.loadAppInsights();
      window.appInsightsEarly = earlyAI;
      
      // Track early platform detection
      const earlyPlatform = {
        userAgent: navigator.userAgent,
        platform: ApplicationInsightsService.detectPlatformFromUA(navigator.userAgent),
        isMobile: /TeamsMobile|iPhone|iPad|Android/i.test(navigator.userAgent),
        isTeams: /Teams|TeamsMobile/i.test(navigator.userAgent),
        url: window.location.href,
        timestamp: new Date().toISOString()
      };
      
      earlyAI.trackEvent({ 
        name: 'EarlyAppLoadStarted', 
        properties: earlyPlatform 
      });
      
      // Add debugging info
      earlyAI.trackEvent({
        name: 'TelemetryDebugInfo',
        properties: {
          ...earlyPlatform,
          connectionString: earlyAI.config.connectionString ? 'present' : 'missing',
          endpointUrl: earlyAI.config.endpointUrl,
          disableTelemetry: earlyAI.config.disableTelemetry,
          environment: process.env.NODE_ENV
        }
      });
      
      console.log('🔍 Early App Insights initialized:', earlyPlatform);
      console.log('🔍 Connection info:', {
        hasConnectionString: !!earlyAI.config.connectionString,
        endpoint: earlyAI.config.endpointUrl
      });
    } catch (error) {
      console.error('Failed to initialize early App Insights:', error);
    }
  }

  /**
   * Initialize Application Insights with Microsoft's recommended configuration
   * @param {Object} history - React Router history object (optional)
   */
  initialize(history = null) {
    // Only initialize if not already initialized
    if (this.isInitialized) {
      console.log('⚠️ Application Insights already initialized');
      return;
    }

    try {
      // Create React plugin per Microsoft documentation
      this.reactPlugin = new ReactPlugin();
      
      // Initialize Application Insights with Microsoft's recommended config
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: 'InstrumentationKey=b1a59456-d7f5-4196-9fca-e4e9907585e3;IngestionEndpoint=https://switzerlandnorth-0.in.applicationinsights.azure.com/;LiveEndpoint=https://switzerlandnorth.livediagnostics.monitor.azure.com/;ApplicationId=b85ea7e7-ff33-481e-82b3-c5f2088aa23b',
          extensions: [this.reactPlugin],
          // React Router integration if history provided
          extensionConfig: history ? {
            [this.reactPlugin.identifier]: { history }
          } : {},
          
          // Microsoft recommended settings for Teams apps
          enableAutoRouteTracking: true,
          disableExceptionTracking: false,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          disableAjaxTracking: false,
          autoTrackPageVisitTime: true,
          
          // Performance tracking
          maxBatchSizeInBytes: 10000,
          maxBatchInterval: 15000,
          
          // Disable telemetry in development
          disableTelemetry: process.env.NODE_ENV === 'development'
        }
      });

      this.appInsights.loadAppInsights();
      
      // Add Teams platform detection per Microsoft documentation
      this.initializePlatformContext();
      
      // Add telemetry initializer for all events
      this.appInsights.addTelemetryInitializer((envelope) => {
        // Add platform context to all telemetry
        if (envelope.baseData) {
          envelope.baseData.properties = envelope.baseData.properties || {};
          if (this.platformContext) {
            envelope.baseData.properties.teamsPlatform = this.platformContext.platform;
            envelope.baseData.properties.teamsHostName = this.platformContext.hostName;
            envelope.baseData.properties.isMobile = this.platformContext.isMobile;
            envelope.baseData.properties.isTeams = this.platformContext.isTeams;
          }
        }
        
        // Always return true to include the telemetry item
        return true;
      });

      this.isInitialized = true;
      console.log('✅ Application Insights initialized successfully');
      
      // Initialize mobile telemetry service
      console.log('🚀 [AppInsights] Initializing mobile telemetry service...');
      mobileTelemetryService.initialize();
      
      // Override console methods for mobile to capture logs
      if (this.platformContext?.isMobile) {
        this.overrideConsoleMethods();
      }
      
      // Track initialization
      this.trackEvent('ApplicationInsightsInitialized', {
        environment: process.env.NODE_ENV,
        timestamp: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('❌ Failed to initialize Application Insights:', error);
    }
  }

  /**
   * Initialize Teams platform context per Microsoft documentation
   * Uses official Teams SDK getContext method
   */
  async initializePlatformContext() {
    try {
      // Early Teams detection based on user agent (more reliable for mobile)
      const isMobileTeams = /TeamsMobile|Android.*Teams|iPhone.*Teams|iPad.*Teams/i.test(navigator.userAgent);
      const isAnyTeams = /Teams|TeamsMobile/i.test(navigator.userAgent);
      
      // Check if Teams SDK is available
      if (typeof teamsApp?.initialize === 'function' && isAnyTeams) {
        try {
          // Use shorter timeout for mobile to prevent hanging
          const timeoutMs = isMobileTeams ? 3000 : 10000;
          
          // Initialize SDK with timeout
          await Promise.race([
            teamsApp.initialize(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('SDK init timeout')), timeoutMs))
          ]);
          
          // Get context using Microsoft's official method
          const context = await Promise.race([
            teamsApp.getContext(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Context timeout')), 2000))
          ]);
          
          // Extract platform information per Microsoft documentation
          this.platformContext = {
            platform: context.app?.host?.clientType || (isMobileTeams ? 'mobile' : 'unknown'),
            hostName: context.app?.host?.name || 'teams',
            isMobile: ['android', 'ios'].includes(context.app?.host?.clientType) || isMobileTeams,
            isDesktop: context.app?.host?.clientType === 'desktop',
            isWeb: context.app?.host?.clientType === 'web',
            isTeams: true, // We confirmed Teams from user agent
            // Additional context
            teamType: context.teamType,
            channelId: context.channel?.id,
            tenantId: context.user?.tenant?.id,
            locale: context.app?.locale,
            theme: context.app?.theme,
            osVersion: context.osVersion,
            sdkVersion: 'v2',
            initMethod: 'sdk_success'
          };
          
          // Track platform detection success
          this.trackEvent('PlatformContextInitialized', this.platformContext);
          
        } catch (sdkError) {
          console.log('Teams SDK failed, using fallback detection:', sdkError.message);
          
          // SDK failed but we're definitely in Teams (from user agent)
          this.platformContext = {
            platform: isMobileTeams ? 'mobile' : 'web',
            hostName: 'teams',
            isMobile: isMobileTeams,
            isDesktop: !isMobileTeams && isAnyTeams,
            isWeb: !isMobileTeams && isAnyTeams,
            isTeams: true, // Still true - we know we're in Teams from user agent
            sdkError: sdkError.message,
            initMethod: 'fallback_with_teams'
          };
          
          this.trackEvent('PlatformContextFallback', {
            ...this.platformContext,
            originalError: sdkError.message
          });
        }
      } else if (isAnyTeams) {
        // In Teams but no SDK available (shouldn't happen)
        this.platformContext = {
          platform: isMobileTeams ? 'mobile' : 'web',
          hostName: 'teams',
          isMobile: isMobileTeams,
          isTeams: true, // We know we're in Teams from user agent
          noSdkReason: 'sdk_not_available',
          initMethod: 'useragent_only'
        };
      } else {
        // Not running in Teams
        this.platformContext = {
          platform: 'web',
          hostName: 'browser',
          isMobile: /Mobile|Android|iPhone/i.test(navigator.userAgent),
          isTeams: false,
          initMethod: 'not_teams'
        };
      }
    } catch (error) {
      console.error('Failed to get Teams context:', error);
      
      // Fallback platform detection - but still check user agent for Teams
      const isAnyTeams = /Teams|TeamsMobile/i.test(navigator.userAgent);
      const isMobileTeams = /TeamsMobile|Android.*Teams|iPhone.*Teams|iPad.*Teams/i.test(navigator.userAgent);
      
      this.platformContext = {
        platform: isMobileTeams ? 'mobile' : 'unknown',
        error: error.message,
        isMobile: /Mobile|Android|iPhone/i.test(navigator.userAgent),
        isTeams: isAnyTeams, // Use user agent detection as last resort
        initMethod: 'error_fallback'
      };
    }
  }

  /**
   * Track debug log (like console.log but for App Insights)
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   */
  trackDebugLog(message, data = {}) {
    this.trackEvent('DebugLog', {
      message,
      data: JSON.stringify(data),
      timestamp: new Date().toISOString(),
      category: 'console'
    });
  }

  /**
   * Track custom event with automatic platform enrichment
   * @param {string} name - Event name
   * @param {Object} properties - Event properties
   */
  trackEvent(name, properties = {}) {
    // Get platform info - use direct detection if context not ready
    const platformInfo = this.platformContext || {
      platform: ApplicationInsightsService.detectPlatformFromUA(navigator.userAgent),
      isMobile: /TeamsMobile|Android.*Teams|iPhone.*Teams|iPad.*Teams/i.test(navigator.userAgent),
      userAgent: navigator.userAgent
    };
    
    // Enrich with platform context
    const enrichedProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      ...platformInfo,
      contextReady: !!this.platformContext
    };

    console.log(`📊 [AppInsights] Tracking: ${name}`, enrichedProperties);

    // Try mobile telemetry first for mobile platforms
    if (platformInfo.isMobile) {
      const sent = mobileTelemetryService.sendTelemetry(name, enrichedProperties);
      if (sent) {
        console.log(`📱 [AppInsights] Sent via mobile telemetry: ${name}`);
        return;
      }
    }

    // Fallback to standard App Insights
    if (!this.appInsights || !this.isInitialized) {
      console.warn(`⚠️ [AppInsights] Not initialized, event lost: ${name}`);
      return;
    }
    this.appInsights.trackEvent({ name, properties: enrichedProperties });
    console.log(`🌐 [AppInsights] Sent directly: ${name}`);
  }

  /**
   * Track exception with context
   * @param {Error} error - The error object
   * @param {Object} properties - Additional properties
   * @param {number} severityLevel - 0=Verbose, 1=Information, 2=Warning, 3=Error, 4=Critical
   */
  trackException(error, properties = {}, severityLevel = 3) {
    const enrichedProperties = {
      ...properties,
      ...this.platformContext,
      timestamp: new Date().toISOString()
    };

    // Try mobile telemetry first for mobile platforms
    if (this.platformContext?.isMobile) {
      mobileTelemetryService.trackException(error, enrichedProperties);
      console.log(`📱 Mobile exception tracked: ${error.message}`);
    }

    // Also send to standard App Insights if available
    if (!this.appInsights || !this.isInitialized) return;
    this.appInsights.trackException({
      exception: error,
      severityLevel,
      properties: enrichedProperties
    });
  }

  /**
   * Track authentication events (critical for mobile debugging)
   * @param {string} action - Authentication action
   * @param {Object} details - Action details
   */
  trackAuthEvent(action, details = {}) {
    // For mobile, also use direct mobile telemetry
    if (this.platformContext?.isMobile) {
      mobileTelemetryService.trackAuthEvent(action, details);
    }
    
    this.trackEvent(`Auth_${action}`, {
      category: 'Authentication',
      ...details
    });
  }

  /**
   * Track API calls for performance monitoring
   * @param {string} method - HTTP method
   * @param {string} url - API endpoint
   * @param {number} status - HTTP status code
   * @param {number} duration - Request duration in ms
   * @param {Error} error - Error object if failed
   * @param {Object} requestData - Request body/params
   * @param {Object} responseData - Response data
   */
  trackApiCall(method, url, status, duration, error = null, requestData = null, responseData = null) {
    // For mobile, include more details like console would show
    const details = {
      method,
      url,
      status,
      duration,
      success: status >= 200 && status < 400,
      error: error?.message,
      category: 'API'
    };
    
    // Add request/response data for mobile debugging
    if (this.platformContext?.isMobile) {
      details.requestBody = requestData ? JSON.stringify(requestData).substring(0, 1000) : 'none';
      details.responseBody = responseData ? JSON.stringify(responseData).substring(0, 1000) : 'none';
      details.errorStack = error?.stack || 'none';
    }
    
    this.trackEvent('APICall', details);
  }

  /**
   * Track user action
   * @param {string} action - User action name
   * @param {Object} details - Action details
   */
  trackUserAction(action, details = {}) {
    this.trackEvent(`UserAction_${action}`, {
      category: 'UserInteraction',
      ...details
    });
  }

  /**
   * Get the React plugin for component tracking
   * @returns {ReactPlugin} The React plugin instance
   */
  getReactPlugin() {
    return this.reactPlugin;
  }

  /**
   * Get the Application Insights instance
   * @returns {ApplicationInsights} The Application Insights instance
   */
  getInstance() {
    return this.appInsights;
  }
  
  /**
   * Track detailed state snapshot (like console.dir)
   * @param {string} context - Where this snapshot is taken
   * @param {Object} state - Current state to capture
   */
  trackStateSnapshot(context, state) {
    if (!this.platformContext?.isMobile) return;
    
    this.trackEvent('StateSnapshot', {
      context,
      snapshot: JSON.stringify(state, null, 2).substring(0, 5000),
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack.split('\n').slice(2, 7).join(' -> ')
    });
  }
  
  /**
   * Override console methods to capture logs on mobile
   */
  overrideConsoleMethods() {
    const originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn
    };
    
    // Override console.log
    console.log = (...args) => {
      originalConsole.log(...args);
      this.trackEvent('ConsoleLog', {
        level: 'log',
        message: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' '),
        timestamp: new Date().toISOString()
      });
    };
    
    // Override console.error
    console.error = (...args) => {
      originalConsole.error(...args);
      this.trackEvent('ConsoleLog', {
        level: 'error',
        message: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' '),
        timestamp: new Date().toISOString(),
        stack: new Error().stack
      });
    };
    
    // Override console.warn
    console.warn = (...args) => {
      originalConsole.warn(...args);
      this.trackEvent('ConsoleLog', {
        level: 'warn',
        message: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' '),
        timestamp: new Date().toISOString()
      });
    };
  }
}

// Create singleton instance
const appInsightsService = new ApplicationInsightsService();
export default appInsightsService;
export { ApplicationInsightsService };